import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField, IconButton, List, ListItem, ListItemText, CircularProgress, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { apiClient } from '../contexts/AuthContext';

interface NotesDialogProps {
  open: boolean;
  onClose: () => void;
  scheduleId: number | null;
  onUpdated?: (notes: NoteItem[]) => void;
}

export interface NoteItem {
  id: number;
  content: string;
  schedule_id?: number;
  created_at?: string;
}

const NotesDialog: React.FC<NotesDialogProps> = ({ open, onClose, scheduleId, onUpdated }) => {
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newContent, setNewContent] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');

  const fetchNotes = useCallback(async () => {
    if (!scheduleId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<NoteItem[]>(`/api/schedules/${scheduleId}/notes`);
      setNotes(res.data);
    } catch (e) {
      setError("Impossible de récupérer les notes.");
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => {
    if (open) {
      fetchNotes();
    }
  }, [open, fetchNotes]);

  const handleAdd = async () => {
    if (!scheduleId || !newContent.trim()) return;
    try {
      await apiClient.post('/api/notes', { schedule_id: scheduleId, content: newContent.trim() });
      setNewContent('');
      await fetchNotes();
      onUpdated?.(notes);
    } catch (e) {
      setError("Erreur lors de l'ajout de la note.");
    }
  };

  const handleDelete = async (noteId: number) => {
    try {
      await apiClient.delete(`/api/notes/${noteId}`);
      await fetchNotes();
      onUpdated?.(notes);
    } catch (e) {
      setError("Erreur lors de la suppression de la note.");
    }
  };

  const handleStartEdit = (note: NoteItem) => {
    setEditingId(note.id);
    setEditingContent(note.content);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingContent('');
  };

  const handleSaveEdit = async () => {
    if (editingId === null) return;
    try {
      // Backend expects `content` likely as query param; send as such
      await apiClient.put(`/api/notes/${editingId}`, null, { params: { content: editingContent } });
      setEditingId(null);
      setEditingContent('');
      await fetchNotes();
      onUpdated?.(notes);
    } catch (e) {
      setError("Erreur lors de la mise à jour de la note.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Notes du planning</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Ajouter une note"
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
          />
          <Button variant="contained" onClick={handleAdd} disabled={!newContent.trim()}>Ajouter</Button>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : error ? (
          <Box sx={{ color: 'error.main', fontSize: 14 }}>{error}</Box>
        ) : (
          <List dense>
            {notes.map((note) => (
              <ListItem key={note.id} secondaryAction={
                editingId === note.id ? (
                  <Box>
                    <Tooltip title="Enregistrer">
                      <IconButton edge="end" onClick={handleSaveEdit}>
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Annuler">
                      <IconButton edge="end" onClick={handleCancelEdit}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box>
                    <Tooltip title="Modifier">
                      <IconButton edge="end" onClick={() => handleStartEdit(note)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton edge="end" onClick={() => handleDelete(note.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )
              }>
                {editingId === note.id ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                  />
                ) : (
                  <ListItemText primary={note.content} secondary={note.created_at ? new Date(note.created_at).toLocaleString() : undefined} />
                )}
              </ListItem>
            ))}
            {notes.length === 0 && (
              <Box sx={{ p: 1, color: 'text.secondary', fontSize: 14 }}>Aucune note.</Box>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotesDialog;

